import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Brain, Shield, Zap, Wind, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { SkillDetailDialog } from './SkillDetailDialog';

interface SkillsLibraryProps {
  user: any;
}

export const SkillsLibrary = ({ user }: SkillsLibraryProps) => {
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [practiceProgress, setPracticeProgress] = useState<Record<string, number>>({});

  const skillCategories = [
    {
      id: 'distress-tolerance',
      name: 'Distress Tolerance',
      icon: Shield,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Skills for getting through crisis moments without making things worse',
      skills: [
        { id: 'tipp', name: 'TIPP (Temperature, Intense exercise, Paced breathing, Progressive relaxation)', difficulty: 'Easy', time: '5-10 min' },
        { id: 'accepts', name: 'ACCEPTS (Activities, Contributing, Comparisons, Emotions, Push away, Thoughts, Sensations)', difficulty: 'Easy', time: '10-20 min' },
        { id: 'improve', name: 'IMPROVE the Moment', difficulty: 'Medium', time: '5-15 min' },
        { id: 'radical-acceptance', name: 'Radical Acceptance', difficulty: 'Hard', time: 'Ongoing' }
      ]
    },
    {
      id: 'emotion-regulation',
      name: 'Emotion Regulation',
      icon: Heart,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Learn to understand, manage, and change emotional responses',
      skills: [
        { id: 'check-facts', name: 'Check the Facts', difficulty: 'Easy', time: '5-10 min' },
        { id: 'opposite-action', name: 'Opposite Action', difficulty: 'Medium', time: '10-30 min' },
        { id: 'please', name: 'PLEASE (Physical health, eating, sleep, exercise)', difficulty: 'Easy', time: 'Daily habit' },
        { id: 'accumulate-positive', name: 'Accumulate Positive Experiences', difficulty: 'Easy', time: 'Daily' }
      ]
    },
    {
      id: 'mindfulness',
      name: 'Mindfulness',
      icon: Wind,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Present moment awareness and non-judgmental observation',
      skills: [
        { id: 'observe', name: 'Observe (Just notice without words)', difficulty: 'Medium', time: '5-10 min' },
        { id: 'describe', name: 'Describe (Put words on experience)', difficulty: 'Easy', time: '5-10 min' },
        { id: 'participate', name: 'Participate (Fully engage)', difficulty: 'Easy', time: 'Ongoing' },
        { id: 'wise-mind', name: 'Wise Mind', difficulty: 'Hard', time: '10-15 min' }
      ]
    },
    {
      id: 'interpersonal',
      name: 'Interpersonal Effectiveness',
      icon: Sparkles,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Skills for maintaining relationships while respecting yourself',
      skills: [
        { id: 'dear-man', name: 'DEAR MAN (Assertive communication)', difficulty: 'Medium', time: '10-15 min' },
        { id: 'give', name: 'GIVE (Relationship maintenance)', difficulty: 'Easy', time: 'In conversation' },
        { id: 'fast', name: 'FAST (Self-respect)', difficulty: 'Medium', time: 'In situation' },
        { id: 'boundaries', name: 'Boundary Setting', difficulty: 'Hard', time: 'Ongoing' }
      ]
    },
    {
      id: 'cognitive',
      name: 'Cognitive Skills',
      icon: Brain,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Tools for identifying and challenging unhelpful thought patterns',
      skills: [
        { id: 'thought-records', name: 'Thought Records', difficulty: 'Medium', time: '10-15 min' },
        { id: 'cognitive-distortions', name: 'Identify Cognitive Distortions', difficulty: 'Easy', time: '5-10 min' },
        { id: 'cognitive-reframing', name: 'Cognitive Reframing', difficulty: 'Medium', time: '10-15 min' },
        { id: 'core-beliefs', name: 'Core Belief Work', difficulty: 'Hard', time: 'Ongoing' }
      ]
    },
    {
      id: 'grounding',
      name: 'Grounding Techniques',
      icon: Zap,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Quick techniques to anchor yourself in the present moment',
      skills: [
        { id: '54321', name: '5-4-3-2-1 Senses', difficulty: 'Easy', time: '2-5 min' },
        { id: 'body-scan', name: 'Body Scan', difficulty: 'Easy', time: '5-15 min' },
        { id: 'safe-place', name: 'Safe Place Visualisation', difficulty: 'Medium', time: '5-10 min' },
        { id: 'grounding-objects', name: 'Grounding Objects', difficulty: 'Easy', time: '1-3 min' }
      ]
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-secondary/20 text-secondary border-secondary/30';
      case 'Medium': return 'bg-primary/20 text-primary-foreground border-primary/30';
      case 'Hard': return 'bg-accent/20 text-background border-accent/30';
      default: return 'bg-muted text-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-light text-foreground mb-3">Skills Library</h3>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Evidence-based psychological skills to help you regulate emotions, tolerate distress, and improve relationships
        </p>
      </div>

      <Tabs defaultValue="distress-tolerance" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 bg-gradient-calm border border-border/50 mb-6 h-auto py-2">
          {skillCategories.map((category) => {
            const Icon = category.icon;
            return (
              <TabsTrigger 
                key={category.id} 
                value={category.id}
                className="gap-2 text-base py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="h-5 w-5" />
                <span className="hidden lg:inline">{category.name}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {skillCategories.map((category) => {
          const Icon = category.icon;
          return (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              <Card className="mb-6 bg-gradient-to-br from-background to-primary/5 border-primary/20">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-3 rounded-lg ${category.bgColor}`}>
                      <Icon className={`h-6 w-6 ${category.color}`} />
                    </div>
                    <CardTitle className="text-2xl">{category.name}</CardTitle>
                  </div>
                  <CardDescription className="text-base">{category.description}</CardDescription>
                </CardHeader>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                {category.skills.map((skill) => {
                  const progress = practiceProgress[skill.id] || 0;
                  return (
                    <Card 
                      key={skill.id}
                      className="hover:shadow-therapy transition-all duration-300 cursor-pointer border-border/50"
                      onClick={() => setSelectedSkill(skill.id)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <CardTitle className="text-lg font-medium leading-tight pr-2">
                            {skill.name}
                          </CardTitle>
                          <Badge className={getDifficultyColor(skill.difficulty)}>
                            {skill.difficulty}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>⏱️ {skill.time}</span>
                          {progress > 0 && (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-4 w-4 text-secondary" />
                              Practised
                            </span>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {progress > 0 && (
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm text-muted-foreground">
                                <span>Practice progress</span>
                                <span>{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-1.5" />
                            </div>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full gap-2"
                            onClick={() => setSelectedSkill(skill.id)}
                          >
                            <span>Learn & Practice</span>
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Quick Stats */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="h-6 w-6 text-primary" />
              <div>
                <p className="text-base font-medium">Your Progress</p>
                <p className="text-sm text-muted-foreground">
                  {Object.keys(practiceProgress).length} skills practised
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              View All Progress
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Skill Detail Dialog */}
      <SkillDetailDialog 
        skillId={selectedSkill} 
        onClose={() => setSelectedSkill(null)} 
      />
    </div>
  );
};
